import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class UtmStorageService {
  private readonly SESSION_STORAGE_KEY = 'utm_tracking_data';
  private readonly COOKIE_PREFIX = 'utm_';
  private readonly CHANNEL_ID_KEY = 'channel_id';
  private readonly TTL_DAYS = 30;
  private platformId = inject(PLATFORM_ID);


  persistTrackingData(channelId?: string, utmParams?: Record<string, string>): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const trackingData: Record<string, string> = {};

    if (channelId) {
      trackingData[this.CHANNEL_ID_KEY] = channelId;
    }

    if (utmParams) {
      Object.keys(utmParams).forEach(key => {
        if (key.toLowerCase().startsWith('utm_')) {
          trackingData[key.toLowerCase()] = utmParams[key];
        }
      });
    }

    try {
      sessionStorage.setItem(this.SESSION_STORAGE_KEY, JSON.stringify(trackingData));
    } catch (error) {
      console.error('Erro ao salvar no sessionStorage:', error);
    }

    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + this.TTL_DAYS);
    const expires = expirationDate.toUTCString();

    Object.keys(trackingData).forEach(key => {
      const cookieName = key === this.CHANNEL_ID_KEY ? this.CHANNEL_ID_KEY : key;
      const cookieValue = encodeURIComponent(trackingData[key]);
      const cookieString = `${cookieName}=${cookieValue}; expires=${expires}; path=/; SameSite=Lax`;

      try {
        document.cookie = cookieString;
      } catch (error) {
        console.error(`Erro ao salvar cookie ${cookieName}:`, error);
      }
    });
  }

  getTrackingData(): { channel_id?: string; utm_params: Record<string, string> } {
    if (!isPlatformBrowser(this.platformId)) {
      return { utm_params: {} };
    }

    let trackingData: Record<string, string> = {};

    try {
      const sessionData = sessionStorage.getItem(this.SESSION_STORAGE_KEY);
      if (sessionData) {
        trackingData = JSON.parse(sessionData);
      }
    } catch (error) {
      console.error('Erro ao ler do sessionStorage:', error);
    }

    if (Object.keys(trackingData).length === 0) {
      trackingData = this.getTrackingDataFromCookies();
    }

    const channelId = trackingData[this.CHANNEL_ID_KEY];
    const utmParams: Record<string, string> = {};

    Object.keys(trackingData).forEach(key => {
      if (key !== this.CHANNEL_ID_KEY && key.toLowerCase().startsWith('utm_')) {
        utmParams[key] = trackingData[key];
      }
    });

    return {
      channel_id: channelId,
      utm_params: utmParams,
    };
  }

  private getTrackingDataFromCookies(): Record<string, string> {
    const trackingData: Record<string, string> = {};

    if (!document.cookie) {
      return trackingData;
    }

    const cookies = document.cookie.split(';');

    cookies.forEach(cookie => {
      const [name, value] = cookie.trim().split('=');
      if (!name || !value) return;

      const decodedValue = decodeURIComponent(value);

      if (name === this.CHANNEL_ID_KEY) {
        trackingData[this.CHANNEL_ID_KEY] = decodedValue;
      }
      else if (name.toLowerCase().startsWith('utm_')) {
        trackingData[name.toLowerCase()] = decodedValue;
      }
    });

    return trackingData;
  }

  clearTrackingData(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      sessionStorage.removeItem(this.SESSION_STORAGE_KEY);
    } catch (error) {
      console.error('Erro ao remover do sessionStorage:', error);
    }

    const trackingData = this.getTrackingData();
    const keysToRemove = [this.CHANNEL_ID_KEY, ...Object.keys(trackingData.utm_params)];

    keysToRemove.forEach(key => {
      const cookieName = key === this.CHANNEL_ID_KEY ? this.CHANNEL_ID_KEY : key;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });
  }
}

